import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ScreenHeader } from "../../../components/PromotionIQ/ScreenHeader";
import { InteractiveCard } from "../../../components/PromotionIQ/InteractiveCard";
import {
  retailers,
  skus,
  baselineDiagnostics,
  formatPercent,
  getRetailerById,
  getSkuById,
} from "../../../data/PromotionIQ/PromotionData";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, AreaChart, Area } from "recharts";
import { ToggleGroup, ToggleGroupItem } from "../../../ui/toggle-group";
import { ChevronRight, Target, TrendingUp } from "lucide-react";
import React from "react";
const BaselineDiagnosticsPage = () => {
  const navigate = useNavigate();
  const [selectedRetailer, setSelectedRetailer] = useState("R1");
  const [selectedSku, setSelectedSku] = useState("S1");
  const [showSyndicated, setShowSyndicated] = useState(false);

  const diagnostic = baselineDiagnostics.find(
    (d) => d.retailer_id === selectedRetailer && d.sku_id === selectedSku
  );

  const retailer = getRetailerById(selectedRetailer);
  const sku = getSkuById(selectedSku);

  // Prepare chart data
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const chartData = months.map((month, idx) => ({
    month,
    baseline: diagnostic?.baseline_curve[idx] || 100,
    seasonality: diagnostic ? Math.round((diagnostic.baseline_curve[idx] || 100) * (diagnostic.seasonality_overlay[idx] || 1)) : 100,
    promo: diagnostic?.promo_weeks.includes(idx + 1),
    syndicated: diagnostic?.syndicated_estimate || 95,
  }));

  const promoWeeks = diagnostic?.promo_weeks || [];
  const nonPromoWeeks = diagnostic?.non_promo_weeks || [];

  return (
    <div className="space-y-6">
      <ScreenHeader
        title="Baseline & Incrementality Diagnostics"
        question="What would have sold anyway?"
      />

      {/* Selectors */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div>
          <label className="label-athena mb-2 block">Retailer</label>
          <select
            value={selectedRetailer}
            onChange={(e) => setSelectedRetailer(e.target.value)}
            className="input-athena"
          >
            {retailers.map((r) => (
              <option key={r.retailer_id} value={r.retailer_id}>
                {r.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-athena mb-2 block">Product Line</label>
          <select
            value={selectedSku}
            onChange={(e) => setSelectedSku(e.target.value)}
            className="input-athena"
          >
            {skus.map((s) => (
              <option key={s.sku_id} value={s.sku_id}>
                {s.variety}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label-athena mb-2 block">View Toggle</label>
          <ToggleGroup 
            type="single" 
            value={showSyndicated ? "syndicated" : "native"} 
            onValueChange={(v) => setShowSyndicated(v === "syndicated")}
            className="h-[38px] w-fit border border-border bg-[hsl(var(--input))] p-0 gap-0 justify-start"
          >
            <ToggleGroupItem value="native" className="px-4 h-full text-sm rounded-none border-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">Native Baseline</ToggleGroupItem>
            <ToggleGroupItem value="syndicated" className="px-4 h-full text-sm rounded-none border-0 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground">Syndicated Estimate</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <InteractiveCard
          label="Confidence Score"
          value={formatPercent(diagnostic?.confidence_score || 0)}
          subValue="Baseline accuracy"
          variant={diagnostic && diagnostic.confidence_score >= 0.8 ? "success" : "warning"}
          linkTo="/promotioniq/learnings"
          linkLabel="See learnings"
        />
        <InteractiveCard
          label="Promo Weeks"
          value={promoWeeks.length}
          subValue="Promotional periods"
          variant="highlight"
          linkTo="/promotioniq/retailer-promotion-truth"
          linkLabel="View truth"
        />
        <InteractiveCard
          label="Non-Promo Weeks"
          value={nonPromoWeeks.length}
          subValue="Clean baseline reads"
          variant="default"
        />
        <InteractiveCard
          label="Syndicated Estimate"
          value={diagnostic?.syndicated_estimate || 0}
          subValue="External data baseline"
          variant="default"
        />
      </div>

      {/* Baseline Curve Chart */}
      <div className="border border-border bg-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="section-header">
            {showSyndicated ? "Syndicated Data Estimate" : "Retailer-Native Baseline Curve"}
          </h3>
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 bg-primary" />
              <span>Baseline</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 bg-success" />
              <span>With Seasonality</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-3 w-3 border-2 border-warning" />
              <span>Promo Week</span>
            </div>
          </div>
        </div>
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(220, 15%, 25%)" />
              <XAxis dataKey="month" stroke="hsl(220, 10%, 55%)" fontSize={12} />
              <YAxis stroke="hsl(220, 10%, 55%)" fontSize={12} />
              <Tooltip
                contentStyle={{ backgroundColor: "hsl(220, 18%, 13%)", border: "1px solid hsl(220, 15%, 25%)", borderRadius: "0" }}
              />
              <Area
                type="monotone"
                dataKey={showSyndicated ? "syndicated" : "baseline"}
                stroke="hsl(32, 95%, 55%)"
                fill="hsl(32, 95%, 55%)"
                fillOpacity={0.3}
                name="Baseline"
              />
              <Line
                type="monotone"
                dataKey="seasonality"
                stroke="hsl(142, 70%, 45%)"
                strokeWidth={2}
                dot={false}
                name="Seasonal Adjusted"
              />
              {promoWeeks.map((week) => (
                <ReferenceLine
                  key={week}
                  x={months[week - 1]}
                  stroke="hsl(45, 93%, 47%)"
                  strokeDasharray="5 5"
                  strokeWidth={2}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Seasonality Overlay */}
      <div className="border border-border bg-card p-6">
        <h3 className="section-header mb-4">Seasonality Multipliers</h3>
        <div className="grid grid-cols-12 gap-2">
          {months.map((month, idx) => {
            const multiplier = diagnostic?.seasonality_overlay[idx] || 1;
            const isPromo = diagnostic?.promo_weeks.includes(idx + 1);
            return (
              <div
                key={month}
                onClick={() => isPromo && navigate("/promotioniq/retailer-promotion-truth")}
                className={`text-center p-3 border transition-all ${
                  isPromo 
                    ? "border-warning bg-warning/10 cursor-pointer hover:bg-warning/20" 
                    : "border-border"
                }`}
              >
                <p className="text-xs text-muted-foreground">{month}</p>
                <p className={`text-lg font-bold ${multiplier >= 1 ? "text-success" : "text-destructive"}`}>
                  {multiplier.toFixed(2)}x
                </p>
                {isPromo && <span className="text-[10px] text-warning">PROMO</span>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Promo vs Non-Promo Comparison */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div 
        onClick={() => navigate("/promotioniq/retailer-promotion-truth")}
          className="border border-border bg-card p-6 cursor-pointer hover:border-primary transition-all group"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="section-header">Promo Week Performance</h3>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
          <div className="space-y-3">
            {promoWeeks.map((week) => (
              <div key={week} className="flex items-center justify-between p-3 bg-warning/10 border border-warning/30">
                <span className="text-muted-foreground">{months[week - 1]}</span>
                <span className="font-bold text-warning">Week {week}</span>
                <span className="text-primary">{diagnostic?.baseline_curve[week - 1]} units</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-border bg-card p-6">
          <h3 className="section-header mb-4">Non-Promo Baseline Reads</h3>
          <div className="text-center p-6 bg-muted/20">
            <p className="text-4xl font-bold text-foreground">
              {Math.round(
                nonPromoWeeks.reduce((sum, week) => sum + (diagnostic?.baseline_curve[week - 1] || 0), 0) / nonPromoWeeks.length
              )}
            </p>
            <p className="text-muted-foreground mt-2">Average Non-Promo Units</p>
          </div>
          <p className="text-sm text-muted-foreground mt-4">
            Based on {nonPromoWeeks.length} clean baseline weeks without promotional interference.
          </p>
        </div>
      </div>

      {/* Related Actions */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div 
          onClick={() => navigate("/promotioniq/retailer-promotion-truth")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-primary" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Promotion Truth</p>
                <p className="text-sm text-muted-foreground">What really happened?</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate("/promotioniq/promotion-builder")}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-5 w-5 text-success" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">Build Investment</p>
                <p className="text-sm text-muted-foreground">Apply baseline insights</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
        <div 
          onClick={() => navigate(`/promotioniq/retailer-economics/${selectedRetailer}`)}
          className="border border-border bg-card p-4 cursor-pointer hover:border-primary hover:bg-primary/5 transition-all group"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-warning" />
              <div>
                <p className="font-medium group-hover:text-primary transition-colors">{retailer?.name} Economics</p>
                <p className="text-sm text-muted-foreground">View retailer profile</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
          </div>
        </div>
      </div>

      {/* Key Insight */}
      <div 
        onClick={() => navigate("/promotioniq/learnings")}
        className="border border-primary/50 bg-primary/5 p-6 cursor-pointer hover:border-primary hover:bg-primary/10 transition-all group"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wide mb-2">Purpose</p>
            <p className="text-lg">
              This positions Promotion IQ as the <span className="text-primary font-bold">incrementality authority</span>, not just a reporter.
              We calculate what would have sold anyway with {formatPercent(diagnostic?.confidence_score || 0)} confidence.
            </p>
          </div>
          <div className="flex items-center gap-2 text-primary opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="text-sm">View Learnings</span>
            <ChevronRight className="h-5 w-5" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BaselineDiagnosticsPage;
